import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from '../../../../../../assets/icons/plus.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import ExpandableCard from '../../../../../../shared/components/Cards/ExpandableCard/ExpandableCard';
import Badge from '../../../../../../shared/components/Globals/Badge/Badge';
import {
  useQuoteActions,
  type IQuoteFlowReturnTarget
} from '../../../../../../shared/hooks/useQuoteActions/useQuoteActions';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import { ProspectStatus } from '../../../../../../shared/types/prospects';
import { getActivationQuotes } from '../../../../../../shared/utils/checks/activationChecks';
import QuotationCard from '../../../ProspectDetail/components/QuotationCard/QuotationCard';
import QuoteFeedback from '../../../ProspectDetail/components/QuoteFeedback/QuoteFeedback';
import './QuotedCard.scss';

interface IQuotedCardProps {
  prospect: IProspect;
  quotes: IQuote[];
  titleKey?: string;
  showNewQuote?: boolean;
  emptyTextKey?: string;
  onQuoteStatusChange?: () => void;
  showTrailingComponent?: boolean;
  returnTo?: IQuoteFlowReturnTarget;
  fromClientProfile?: boolean;
  acceptDisabledTooltip?: string;
}

const QuotedCard = ({
  prospect,
  quotes,
  titleKey = TRANSLATIONS_PROSPECTS.detail.quoted.sectionLabel,
  showNewQuote = true,
  emptyTextKey = TRANSLATIONS_PROSPECTS.detail.pendingQuote.noQuotesHint,
  onQuoteStatusChange,
  showTrailingComponent = true,
  returnTo,
  fromClientProfile = false,
  acceptDisabledTooltip
}: IQuotedCardProps) => {
  const { t } = useTranslation();

  const {
    localQuotes,
    toast,
    setToast,
    unacceptTarget,
    acceptingQuoteId,
    downloadingQuoteId,
    handleNewQuote,
    handleEditQuote,
    handleDownloadQuote,
    handleAcceptQuote,
    handleConfirmUnaccept,
    handleCancelUnaccept
  } = useQuoteActions(quotes, {
    onAcceptSuccess: onQuoteStatusChange,
    onUnacceptSuccess: onQuoteStatusChange,
    returnTo,
    fromClientProfile
  });

  const acceptedQuotes = getActivationQuotes(quotes);
  const trailingComponent =
    prospect.status === ProspectStatus.Quoted && showTrailingComponent ? (
      <Badge variant="inactive">
        {t(TRANSLATIONS_PROSPECTS.detail.quoted.acceptedQuotes, {
          accepted: acceptedQuotes.length,
          quotes: quotes.length
        })}
      </Badge>
    ) : null;

  return (
    <ExpandableCard
      title={t(titleKey)}
      className="quoted-card"
      trailingComponent={trailingComponent}
      defaultExpanded={false}
    >
      <div className="quoted-card__body">
        {localQuotes.length > 0 ? (
          <div className="quoted-card__list">
            {localQuotes.map((quote) => (
              <QuotationCard
                key={quote.id}
                quote={quote}
                onAccept={(qid) => handleAcceptQuote(prospect.id, qid)}
                onEdit={(qid) => handleEditQuote(prospect.id, qid)}
                onDownload={(qid) => handleDownloadQuote(prospect.id, qid)}
                isAccepting={acceptingQuoteId === quote.id}
                isDownloading={downloadingQuoteId === quote.id}
                acceptDisabledTooltip={acceptDisabledTooltip}
              />
            ))}
          </div>
        ) : (
          <p className="quoted-card__empty">{t(emptyTextKey)}</p>
        )}

        {showNewQuote && (
          <div className="quoted-card__footer">
            <CustomButton
              variant="default"
              size="s"
              className="quoted-card__new-quote"
              onClick={() => handleNewQuote(prospect.id)}
            >
              <PlusIcon className="quoted-card__plus" />
              {t(TRANSLATIONS_PROSPECTS.detail.quoted.newQuote)}
            </CustomButton>
          </div>
        )}
      </div>

      <QuoteFeedback
        prospect={prospect}
        toast={toast}
        setToast={setToast}
        unacceptTarget={unacceptTarget}
        onCancelUnaccept={handleCancelUnaccept}
        onConfirmUnaccept={(pid) => handleConfirmUnaccept(pid)}
      />
    </ExpandableCard>
  );
};

export default QuotedCard;
